import { useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PrimaryButton } from '@pulse-web-ui/button-v2';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import type { PersonalDocuments } from '@entities/agent-contract';
import {
  profileSectionToAnalyticEvent,
  useUpdateProfileDraft,
} from '@entities/agent-profile';
import type {
  ContactsValues,
  UpdateProfileDraftRequestParams,
} from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  FlkCode,
  PROFILE,
  ProfileSection,
} from '@shared/constants';
import { useStores } from '@shared/hooks';
import {
  addTestAttribute,
  getMismatches,
  sendRequestErrorsAnalytics,
} from '@shared/utils';

export const SubmitContactsDraft = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();
  const {
    MainStore: {
      agentContractStore: { updateAgentProfileDraft, agentProfileDraft },
    },
  } = useStores();

  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = useFormContext<ContactsValues>();

  const [requestParams, setRequestParams] =
    useState<UpdateProfileDraftRequestParams>({
      agentProfileChange: {
        ...agentProfileDraft,
        email: undefined,
        personalDocument: {
          ...(agentProfileDraft?.personalDocument as PersonalDocuments),
          website: undefined,
        },
      },
    });

  const {
    res,
    refetch: updateProfileDraft,
    error,
    isFetching,
  } = useUpdateProfileDraft(requestParams, [requestParams]);

  useEffect(() => {
    if (error) {
      const mismatches = getMismatches(error);
      sendRequestErrorsAnalytics(
        AnalyticEvent.AGENT_EDITPERSONALDATA_OPEN_ERROR,
        mismatches
      );

      if (!!mismatches) {
        let hasKnownErrors = false;

        mismatches.forEach(({ code }) => {
          if (code === FlkCode.AGT_4801_0004) {
            // Пользователь с таким email существует
            setError('email', {
              message: t('COMMON:errors.emailAlreadyExist'),
            });
            hasKnownErrors = true;
          }
        });

        if (!hasKnownErrors) {
          showBoundary(BoundaryErrorType.SomethingWentWrong);
        }
      } else {
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    }
  }, [error]);

  useEffect(() => {
    if (
      !isFetching &&
      res &&
      !!requestParams?.agentProfileChange?.email?.length
    ) {
      updateAgentProfileDraft(requestParams.agentProfileChange);
      navigate(PROFILE);
    }
  }, [requestParams, isFetching, res]);

  useEffect(() => {
    if (!!requestParams?.agentProfileChange?.email?.length) {
      sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_SAVE, {
        category: profileSectionToAnalyticEvent[ProfileSection.CONTACTS],
      });

      updateProfileDraft();
    }
  }, [requestParams]);

  const submitPage = handleSubmit((values) => {
    const { email, website } = values;

    setRequestParams((prevParams: UpdateProfileDraftRequestParams) => ({
      ...prevParams,
      agentProfileChange: {
        ...prevParams.agentProfileChange,
        email,
        personalDocument: {
          ...(prevParams.agentProfileChange
            .personalDocument as PersonalDocuments),
          website,
        },
      },
    }));
  });

  return (
    <PrimaryButton
      size='l'
      onClick={submitPage}
      disabled={!!Object.keys(errors).length}
      isLoading={isFetching}
      {...addTestAttribute('contactsDraft.submitButton')}
    >
      {t('COMMON:buttons.save')}
    </PrimaryButton>
  );
});
